/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.quickOrderList = this.querySelector('.js-quick-order-list');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);
      
      // Initialize free product validation
      this.initFreeProductValidation();

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.addEventListener('on:quick-order-list:update', this.refreshQuickOrderList.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    initFreeProductValidation() {
      // Get free product variant ID from global theme settings
      const variantIdString = theme.settings.cartProgressBar?.freeProductVariantId;
      this.freeProductVariantId = variantIdString ? parseInt(variantIdString, 10) : null;
      
      // Only proceed if we have a free product configured
      if (!this.freeProductVariantId || this.freeProductVariantId === 0) {
        return;
      }

      console.log('[QuickAdd] Freebie validation active — variantId:', this.freeProductVariantId);
      
      // Override the addToCart method to check for free product
      const originalAddToCart = this.addToCart;
      this.addToCart = async (variantId) => {
        if (variantId === this.freeProductVariantId) {
          console.log('Quick Add: Intercepted add to cart for free product');
          
          // Check if already in cart using fresh cart data
          const alreadyInCart = await this.checkIfFreeProductInCart();
          if (alreadyInCart) {
            console.log('Quick Add: Free product already in cart, preventing addition');
            return;
          }
        }
        
        // Call original method if not blocked
        return originalAddToCart.call(this, variantId);
      };
    }

    async checkIfFreeProductInCart() {
      try {
        // Fetch fresh cart JSON to check if free product line item exists
        const response = await fetch(`${theme.routes.cart}.js`);
        if (response.ok) {
          const cartData = await response.json();
          const freeProductItem = cartData.items.find(item => item.variant_id.toString() === this.freeProductVariantId);
          const hasFreeProduct = !!freeProductItem;
          console.log('Quick Add: Free product line item in fresh cart data:', hasFreeProduct);
          if (freeProductItem) {
            console.log('Quick Add: Free product found with quantity:', freeProductItem.quantity, 'at line index:', freeProductItem.index);
          }
          return hasFreeProduct;
        }
      } catch (error) {
        console.error('Quick Add: Error checking free product in cart:', error);
      }
      
      // Fallback to DOM check if API fails
      const cartItems = document.querySelectorAll('.cart-item');
      for (let item of cartItems) {
        const variantId = item.dataset.variantId;
        if (variantId === this.freeProductVariantId) {
          return true;
        }
      }
      return false;
    }

    /**
     * Improved removal function using line item keys
     * @param {number} variantId - The variant ID to remove
     * @returns {Promise<Object>} Updated cart data
     */
    async removeAllByVariantId(variantId) {
      console.log('Quick Add: Removing all items with variant ID:', variantId);
      
      try {
        // 1) Get current cart
        const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
        console.log('Quick Add: Current cart items:', cart.items.length);

        // 2) Find all line item keys for this variant
        const keysToRemove = cart.items
          .filter(item => item.variant_id === variantId)
          .map(item => item.key); // key uniquely identifies the line (variant + properties + selling plan, etc.)

        console.log('Quick Add: Found items to remove:', keysToRemove.length);
        
        if (keysToRemove.length === 0) {
          console.log('Quick Add: No items found to remove');
          return cart; // nothing to remove
        }

        // 3) Remove each matching line by key
        for (const key of keysToRemove) {
          console.log('Quick Add: Removing line key:', key);
          
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 }) // using line-item key ensures exact match
          });
          
          if (!res.ok) {
            const errorText = await res.text();
            throw new Error(`Failed to remove line key ${key}: ${errorText}`);
          }
          
          console.log('Quick Add: Successfully removed line key:', key);
        }

        // 4) Return final cart
        const finalCart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
        console.log('Quick Add: Final cart items:', finalCart.items.length);
        
        return finalCart;
        
      } catch (error) {
        console.error('Quick Add: Error removing items by variant ID:', error);
        throw error;
      }
    }

    /**
     * Handle free product milestone logic
     * @param {Object} cartData - Current cart data
     */
    async handleFreeProductMilestone(cartData) {
      console.log('Quick Add: ===== HANDLING FREE PRODUCT MILESTONE =====');
      console.log('Quick Add: Cart data:', cartData);
      
      // Calculate cart total excluding free product
      const cartTotalExcludingFreeProduct = this.calculateCartTotalExcludingFreeProduct(cartData);
      console.log('Quick Add: Cart total excluding free product:', cartTotalExcludingFreeProduct, 'cents = $', (cartTotalExcludingFreeProduct / 100));
      
      // Check if free product is currently in cart
      const freeProductItem = cartData.items.find(item => item.variant_id === this.freeProductVariantId);
      let hasFreeProduct = !!freeProductItem;
      
      console.log('Quick Add: Has free product in cart:', hasFreeProduct);
      if (freeProductItem) {
        console.log('Quick Add: Free product details:', freeProductItem);
      }
      
      // Check for 3rd milestone popup products to remove
      const milestone3PopupItems = cartData.items.filter(item => 
        item.properties && item.properties._3rd_milestone_popup === 'true'
      );
      
      console.log('Quick Add: Found', milestone3PopupItems.length, '3rd milestone popup products in cart');
      
      // Get milestone thresholds from global settings (in dollars, convert to cents)
      const milestone2Threshold = (theme.settings.cartProgressBar?.milestone2 || 10) * 100; // Default $10
      const milestone3Threshold = (theme.settings.cartProgressBar?.milestone3 || 15) * 100; // Default $15
      
      console.log('🎯 Quick Add: ===== FREEBIE THRESHOLDS =====');
      console.log('🎯 Quick Add: Milestone 2 (Freebie 1) threshold:', milestone2Threshold, 'cents = $', (milestone2Threshold / 100));
      console.log('🎯 Quick Add: Milestone 3 (Freebie 2) threshold:', milestone3Threshold, 'cents = $', (milestone3Threshold / 100));
      console.log('🎯 Quick Add: Cart total (excluding freebie):', cartTotalExcludingFreeProduct, 'cents = $', (cartTotalExcludingFreeProduct / 100));
      
      // Handle removals: Check if both freebies need to be removed (below both thresholds)
      let updatedCartData = cartData;
      let needsFreebie1Removal = false;
      
      // Check if both need to be removed - do them together for efficiency
      const needsMilestone3Removal = cartTotalExcludingFreeProduct < milestone3Threshold && milestone3PopupItems.length > 0;
      needsFreebie1Removal = cartTotalExcludingFreeProduct < milestone2Threshold && hasFreeProduct;
      
      // Remove 3rd milestone popup products if below threshold
      if (needsMilestone3Removal) {
        console.log('Quick Add: 🚨 MILESTONE 3 BELOW THRESHOLD! Removing', milestone3PopupItems.length, 'popup products');
        updatedCartData = await this.removeMilestone3PopupProducts(cartData, milestone3PopupItems);
        // Update hasFreeProduct from the returned cart data (no need to re-fetch)
        if (updatedCartData && updatedCartData.items) {
          const freebie1AfterRemoval = updatedCartData.items.find(item => item.variant_id === this.freeProductVariantId);
          hasFreeProduct = !!freebie1AfterRemoval;
          needsFreebie1Removal = cartTotalExcludingFreeProduct < milestone2Threshold && hasFreeProduct;
          console.log('Quick Add: After milestone 3 removal, freebie 1 still in cart:', hasFreeProduct);
        }
      }
      
      // Milestone logic: Add free product at milestone2 threshold, remove below threshold
      // Check this AFTER milestone 3 removal to ensure both are handled
      if (cartTotalExcludingFreeProduct >= milestone2Threshold) {
        // Should have free product
        if (!hasFreeProduct) {
          console.log('Quick Add: 🎉 MILESTONE $' + (milestone2Threshold / 100) + '+ REACHED! Adding free product');
          const freeProductResult = await this.addFreeProductToCart();
          
          // Additional UI updates after free product is added
          if (freeProductResult) {
            // Update cart page if we're on the cart page
            if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                cartItems.refreshCartItems();
              }
            }
            
            if (typeof window.ccDispatchCartChange === 'function') {
              window.ccDispatchCartChange({
                cart: freeProductResult,
                newTotalQuantity: freeProductResult.item_count,
                newTotalPrice: freeProductResult.total_price
              });
            }
          }
        } else {
          console.log('Quick Add: ✅ MILESTONE $' + (milestone2Threshold / 100) + '+ REACHED! Free product already exists');
        }
      } else {
          // Should not have free product - remove if it exists
          if (needsFreebie1Removal || hasFreeProduct) {
            console.log('Quick Add: 🚨 MILESTONE BELOW $' + (milestone2Threshold / 100) + '! Removing freebie 1');
            
            // Remove freebie 1 using the updated cart data (after milestone 3 removal if it happened)
          await this.removeAllByVariantId(this.freeProductVariantId);
            // Return the updated cart data directly - no need for another fetch
            return updatedCartData;
        } else {
            console.log('Quick Add: ✅ MILESTONE BELOW $' + (milestone2Threshold / 100) + '! No free product to remove');
        }
      }
    }

    async removeMilestone3PopupProducts(cartData, popupItems) {
      try {
        console.log('Quick Add: ===== REMOVING 3RD MILESTONE POPUP PRODUCTS =====');
        console.log('Quick Add: Found', popupItems.length, 'popup products to remove');
        
        // Remove all popup products in parallel for speed (using Promise.all)
        const removalPromises = popupItems.map(async (item) => {
          try {
          console.log('Quick Add: Removing popup product:', item.title, 'Key:', item.key);
          
            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            });

            if (!response.ok) {
              const errorText = await response.text();
              // If 400/404, item was already removed - that's okay
              if (response.status === 400 || response.status === 404) {
                console.log('Quick Add: Item already removed (status ' + response.status + '), continuing:', item.title);
                return null;
              }
              throw new Error(`HTTP error! status: ${response.status}, ${errorText}`);
            }

            const result = await response.json();
            console.log('Quick Add: Successfully removed popup product:', item.title);
            return result;
            
          } catch (error) {
            console.error('Quick Add: Error removing popup product:', item.title, error);
            return null; // Return null on error, we'll continue
          }
        });
        
        // Wait for all removals to complete
        await Promise.all(removalPromises);
        
        console.log('Quick Add: ✅ SUCCESS! All 3rd milestone popup products removed');
        
        // Clear session flags to allow popup to show again
        sessionStorage.removeItem('milestone3-popup-product-selected');
        sessionStorage.removeItem('milestone3-popup-shown');
        console.log('Quick Add: Cleared popup session flags - popup can show again');
        
        // Fetch fresh cart data after removal to get accurate count
        const freshCartResponse = await fetch('/cart.js');
        const freshCartData = await freshCartResponse.json();
        
        // Update cart bubble count
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const countElement = cartIconBubble.querySelector('.header__cart-count-number');
          const countText = cartIconBubble.querySelector('.visually-hidden');
          
          if (countElement) {
            countElement.textContent = freshCartData.item_count;
            console.log('Quick Add: Cart bubble count updated to:', freshCartData.item_count, 'after removing popup products');
          }
          
          if (countText) {
            const itemText = freshCartData.item_count === 1 ? 'item' : 'items';
            countText.textContent = `${freshCartData.item_count} ${itemText}`;
          }
          
          // Show/hide cart count based on whether cart is empty
          const cartCountContainer = cartIconBubble.querySelector('.header__cart-count');
          if (cartCountContainer) {
            if (freshCartData.item_count > 0) {
              cartCountContainer.style.display = '';
            } else {
              cartCountContainer.style.display = 'none';
            }
          }
        }
        
        // Trigger UI updates - refresh cart items on cart page
        if (window.location.pathname === theme.routes.cart) {
          const cartItems = document.querySelector('cart-items');
          if (cartItems) {
            console.log('Quick Add: Refreshing cart items on cart page after popup product removal');
            cartItems.refreshCartItems();
          }
        }
        
        // Update cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.refreshCartDrawer) {
          console.log('Quick Add: Refreshing cart drawer after popup product removal');
          cartDrawer.refreshCartDrawer();
        }
        
        if (typeof window.ccDispatchCartChange === 'function') {
          window.ccDispatchCartChange({
            source: 'milestone3_removal',
            action: 'popup_products_removed',
            removedItems: popupItems.length,
            cart: updatedCart || cartData
          });
        }
        
      } catch (error) {
        console.error('Quick Add: Error removing 3rd milestone popup products:', error);
      }
    }

    /**
     * Calculate cart total excluding free product
     * @param {Object} cartData - Cart data object
     * @returns {number} Total in cents excluding free product
     */
    calculateCartTotalExcludingFreeProduct(cartData) {
      if (!cartData || !cartData.items) {
        console.log('Quick Add: No cart data or items found');
        return 0;
      }

      console.log('Quick Add: ===== CALCULATING CART TOTAL EXCLUDING FREE PRODUCT =====');
      console.log('Quick Add: Free product variant ID to exclude:', this.freeProductVariantId);

      // Use the exact same simple calculation as CartItems
      // Also exclude products with _3rd_milestone_popup property
      const totalExcludingFreeProduct = cartData.items
        .filter(item => {
          const isFreeProduct = item.variant_id === this.freeProductVariantId;
          const isMilestonePopupProduct = item.properties && item.properties._3rd_milestone_popup === 'true';
          const shouldExclude = isFreeProduct || isMilestonePopupProduct;
          
          if (shouldExclude) {
            console.log('Quick Add: Excluding item:', item.title, isFreeProduct ? '(Free Product)' : '(Milestone Popup Product)');
          }
          
          return !shouldExclude;
        })
        .reduce((total, item) => total + (item.final_line_price), 0);
      
      console.log('Quick Add: Cart total excluding free product and milestone popup products:', totalExcludingFreeProduct, 'cents = $', (totalExcludingFreeProduct / 100));
      
      return totalExcludingFreeProduct;
    }

    /**
     * Add free product to cart
     */
    async addFreeProductToCart() {
      console.log('Quick Add: Adding free product with variant ID:', this.freeProductVariantId);

      if (!window.freeProductLock) {
        window.freeProductLock = { isProcessing: false, variantId: null };
      }
      if (window.freeProductLock.isProcessing) {
        console.log('Quick Add: Global free product lock active, skipping add');
        return null;
      }
      window.freeProductLock.isProcessing = true;
      window.freeProductLock.variantId = this.freeProductVariantId;

      try {
        // CRITICAL: Check if free product is already in cart before adding
        console.log('Quick Add: Performing final atomic check before adding free product');
        const freshCartCheck = await fetch(`${theme.routes.cart}.js`);
        if (!freshCartCheck.ok) {
          console.error('Quick Add: Failed to fetch cart for final check, aborting addition');
          return;
        }
        
        let freshCartData = await freshCartCheck.json();
        const existingFreeProductItem = freshCartData.items.find(item => 
          item.variant_id.toString() === this.freeProductVariantId.toString()
        );
        
        if (existingFreeProductItem) {
          // Check if it's already a freebie (has freebie properties)
          const isAlreadyFreebie = existingFreeProductItem.properties && 
            (existingFreeProductItem.properties._free_product === 'true' || 
             existingFreeProductItem.properties['_free_product'] === 'true');
          
          if (isAlreadyFreebie) {
            console.log('Quick Add: ✅ FREE PRODUCT ALREADY EXISTS AS FREEBIE in cart! Skipping addition to prevent duplicate.');
            return;
          } else {
            // Variant exists but NOT as a freebie - remove it first, then add as freebie
            console.log('Quick Add: Variant exists as normal product (not freebie). Removing it first, then adding as freebie.');
            console.log('Quick Add: Existing item properties:', existingFreeProductItem.properties);
            const updatedCart = await this.removeAllByVariantId(this.freeProductVariantId);
            // Continue with the addition below
          }
        }
        
        // Use the same approach as CartItems for consistency
        const formData = new FormData();
        formData.append('id', this.freeProductVariantId);
        formData.append('quantity', '1');
        
        // Add Kite promo properties for first freebie
        formData.append('properties[_free_product]', 'true');
        formData.append('properties[_rule_id]', 'SPEND_X_0');
        formData.append('properties[_kite_promo_name]', 'Freebie 1');
        
        formData.append('sections', 'cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);
        
        // Add cart drawer section if it exists
        if (this.cartDrawer) {
          const cartDrawerSection = this.cartDrawer.closest('.shopify-section');
          if (cartDrawerSection) {
            formData.append('sections', cartDrawerSection.id.replace('shopify-section-', ''));
          }
        }
        
        console.log('Quick Add: Making API call to add free product');
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Quick Add: ✅ SUCCESS! Free product added to cart');
          
          // Update cart icon count using the response data
          if (data.sections && data.sections['cart-icon-bubble']) {
            const cartIconBubble = document.getElementById('cart-icon-bubble');
            if (cartIconBubble) {
              cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
            }
          }
          
          // Update cart drawer if it exists
          if (this.cartDrawer) {
            this.cartDrawer.refreshCartDrawer();
          }
          
          if (typeof window.ccDispatchCartChange === 'function') {
            window.ccDispatchCartChange({
              cart: data,
              newTotalQuantity: data.item_count,
              newTotalPrice: data.total_price
            });
          }
          
        } else {
          const errorData = await response.text();
          console.error('Quick Add: ❌ FAILED to add free product. Status:', response.status, 'Error:', errorData);
        }
      } catch (error) {
        console.error('Quick Add: Error adding free product:', error);
      } finally {
        if (window.freeProductLock) {
          window.freeProductLock.isProcessing = false;
          window.freeProductLock.variantId = null;
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    async handleDocumentClick(evt) {
      if (!(evt.target instanceof Element)) return;
      if (!evt.target.matches('.js-quick-add')) return;

      await this.addProductToCart(evt.target);
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Refreshes the Quick Order List element.
     * @param {object} evt - Event object.
     */
    async refreshQuickOrderList(evt) {
      if (evt.detail.productId === this.productId) {
        const qbQuickOrderList = this.querySelector('quick-order-list');
        // Re-fetch the product page and extract the quick order list
        this.fetch = {
          url: this.productUrl,
          promise: fetch(this.productUrl)
        };

        const response = await this.fetch.promise;
        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();
          const productEl = tmpl.content.querySelector('.js-product');
          const quickOrderList = productEl.querySelector('quick-order-list');
          if (quickOrderList && qbQuickOrderList) {
            qbQuickOrderList.innerHTML = quickOrderList.innerHTML;
          }
        }

        this.fetch = null;
        qbQuickOrderList.disableLoading();
      }
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (
        this.productUrl
        && this.productUrl === opener.dataset.productUrl
        && (!this.quickOrderList || this.quickOrderList.innerHTML.length === 0)) {
        super.open(opener);
        if (opener.dataset.selectedColor) this.setActiveVariant(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.productId = opener.dataset.productId;
      this.content.innerHTML = '';
      this.quickOrderList.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector('.cc-main-product .js-product');
        this.renderProduct(opener);
      }

      this.fetch = null;

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      // Reset the section on quick-order-list
      const quickOrderList = this.productEl.querySelector('quick-order-list');
      if (quickOrderList) quickOrderList.setAttribute('data-section', sectionId);

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) variantPicker.dataset.updateUrl = 'false';

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = this.productEl.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }

      this.updateContent();
      this.updateForm();

      // Update the product media.
      const activeMedia = this.productEl.querySelector('.media-viewer__item.is-current-variant');
      if (activeMedia) this.updateMedia(activeMedia.dataset.mediaId);

      if (opener.dataset.selectedColor) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
      window.initLazyImages();
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId) {
      const img = this.productEl.querySelector(`[data-media-id="${mediaId}"] img`);
      if (!img) return;

      const src = img.src ? img.src.split('&width=')[0] : img.dataset.src.split('&width=')[0];
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = img.width / img.height;

      container.innerHTML = `
        <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${img.alt}">
      `;
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      let weightElem = this.getElementHtml('.product-info__weight:not(.product-info__title-weight)');
      if (weightElem && weightElem.length > 0) {
        weightElem = `<div class="product-info__weight text-sm mt-2">${weightElem}</div>`;
      }

      this.content.innerHTML = `
        <div class="quick-add-info grid mb-8">
          <div class="quick-add-info__media${theme.settings.blendProductImages ? ' image-blend' : ''}"></div>
          <div class="quick-add-info__details">
            <div class="product-vendor-sku mb-2 text-sm">
              ${this.getElementHtml('.product-vendor-sku')}
            </div>
            <div class="product-title">
              <a class="h6 js-prod-link" href="${this.productUrl}">
                ${this.getElementHtml('.product-title')}
              </a>
            </div>
            ${weightElem}
            <div class="product-price">
              ${this.getElementHtml('.product-price')}
            </div>
            <div class="text-theme-light text-sm mt-4">
              <a href="${this.productUrl}" class="link js-prod-link">
                ${theme.strings.viewDetails}
              </a>
            </div>
          </div>
          <div class="quick-add-info__details md:hidden"></div>
        </div>
        <div class="product-options">
          ${this.getElementHtml('.product-options')}
        </div>
        <div class="product-backorder">
          ${this.getElementHtml('.product-backorder')}
        </div>
        <script type="application/json" class="js-inventory-data">
          ${this.getElementHtml('.js-inventory-data')}
        </script>
      `;

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      this.footer.classList.remove('quick-add__footer-message');

      const newQuickOrderList = this.productEl.querySelector('.js-quick-order-list');
      if (!productForm && newQuickOrderList) {
        this.quickOrderList.innerHTML = newQuickOrderList.innerHTML;
      }
      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else if (newQuickOrderList) {
        // Append the quick order script
        const script = document.createElement('script');
        script.src = newQuickOrderList.querySelector('script:first-of-type').src;
        document.head.appendChild(script);

        this.footer.hidden = true;
      } else {
        const signUpForm = this.productEl.querySelector('.product-signup');
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add('quick-add__footer-message');
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Gets the innerHTML of an element within the product element.
     * @param {string} selector - CSS selector for the element.
     * @returns {?string}
     */
    getElementHtml(selector) {
      const el = this.productEl.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }

    /**
     * Adds product directly to cart without opening drawer
     * @param {Element} button - The quick add button that was clicked
     */
    async addProductToCart(button) {
      const productId = button.dataset.productId;
      const variantId = button.dataset.variantId;

      // Shared ATC timer — all sub-steps reference window.__atcT0
      window.__atcT0 = performance.now();
      const _atcLog = (step, extra) => {
        const ms = (performance.now() - window.__atcT0).toFixed(0);
        const suffix = extra ? ' ' + JSON.stringify(extra) : '';
        console.log(`[ATC +${ms}ms] ${step}${suffix}`);
      };

      _atcLog('🚀 START (quick-add)', { variant: variantId });

      // Validate we have the required data
      if (!variantId) {
        console.error('No variant ID found for product');
        return;
      }

      // Check for quantity limit before proceeding
      const maxQuantity = button.dataset.maxQuantity ? parseInt(button.dataset.maxQuantity, 10) : null;
      if (maxQuantity) {
        try {
          _atcLog('📥 qty-limit check: /cart.js START');
          const cartCheckResponse = await fetch(`${theme.routes.cart}.js`);
          const cartData = await cartCheckResponse.json();
          _atcLog('📥 qty-limit check: /cart.js DONE', { item_count: cartData.item_count });
          const cartItem = cartData.items.find(item => item.variant_id.toString() === variantId.toString());
          const currentCartQty = cartItem ? cartItem.quantity : 0;
          const newTotal = currentCartQty + 1;

          if (newTotal > maxQuantity) {
            _atcLog('🚫 qty-limit exceeded, aborting', { newTotal, maxQuantity });
            // Show the quantity limit modal
            if (window.quantityLimitManager) {
              window.quantityLimitManager.showModal(`You can only buy ${maxQuantity} ${maxQuantity === 1 ? 'unit' : 'units'} of this item.`);
            } else {
              alert(`You can only buy ${maxQuantity} ${maxQuantity === 1 ? 'unit' : 'units'} of this item.`);
            }
            return; // Stop the add to cart
          }
        } catch (error) {
          console.error('Error checking quantity limit:', error);
        }
      } else {
        _atcLog('⏭️ qty-limit check skipped (no maxQuantity)');
      }

      // Set loading state on button (same as ProductForm)
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      // Store original button content
      const originalButtonContent = button.innerHTML;

      // Enable cart loading states
      this.enableCartLoading();

      // Build sections string the same way product-form.js does — so POST response includes drawer HTML
      let sectionsParam = 'cart-icon-bubble';
      if (this.cartDrawer) {
        const drawerSection = this.cartDrawer.closest('.shopify-section');
        if (drawerSection) sectionsParam += `,${drawerSection.id.replace('shopify-section-', '')}`;
      }
      _atcLog('ℹ️ sections in POST', { sections: sectionsParam });

      const openDrawer = !!(this.cartDrawer && (
        (theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md)
        || theme.settings.afterAtc === 'drawer'
      ));

      try {
        _atcLog('🛒 POST /cart/add START');
        // POST with sections so the response includes fully-rendered drawer HTML (same as product-form.js)
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', sectionsParam);
        formData.append('sections_url', window.location.pathname);

        const cartResponse = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        _atcLog('🛒 POST /cart/add DONE', { status: cartResponse.status, ok: cartResponse.ok });

        if (cartResponse.ok) {
          _atcLog('📄 parsing POST response JSON START');
          const data = await cartResponse.json();
          _atcLog('📄 parsing POST response JSON DONE', { hasSections: !!(data && data.sections), item_count: data.item_count });

          // Update cart icon bubble from POST sections
          if (data.sections && data.sections['cart-icon-bubble']) {
            const cartIconBubble = document.getElementById('cart-icon-bubble');
            if (cartIconBubble) {
              cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
              _atcLog('🔵 cart-icon-bubble updated from POST sections');
            }
          }

          // Update cart page title count — pass existing cartData, no extra fetch
          if (window.location.pathname === theme.routes.cart) {
            this.updateCartPageCount(data);
          }

          // Fire /cart.js in parallel while we render (same pattern as product-form.js)
          _atcLog('📥 /cart.js fetch START (parallel)');
          const newCartPromise = fetch(`${theme.routes.cart}.js`).then(r => r.json());

          // Fire freebie check in background — don't block drawer render
          _atcLog('🎁 freebie check fired (background)');
          const cartItems = document.querySelector('cart-items');
          const _freebiePromise = (cartItems && typeof cartItems.checkAndHandleFreeProductImmediately === 'function')
            ? cartItems.checkAndHandleFreeProductImmediately(data)
            : this.handleFreeProductMilestone(data);
          _freebiePromise.catch(err => console.error('Quick Add: freebie check error:', err));

          // Render drawer directly from POST sections — no extra sections fetch
          if (this.cartDrawer) {
            _atcLog('🗂️ renderContents START (from POST sections)');
            await this.cartDrawer.renderContents(data, openDrawer);
            _atcLog('🗂️ renderContents DONE');
            if (typeof CartDrawer !== 'undefined' && CartDrawer.recalculateCssVarHeights) {
              CartDrawer.recalculateCssVarHeights();
            }
          } else if (window.location.pathname === theme.routes.cart) {
            _atcLog('🗂️ cart page refresh START');
            const cartItemsEl = document.querySelector('cart-items');
            if (cartItemsEl) {
              if (cartItemsEl.dataset.empty === 'true') {
                window.location.reload();
              } else {
                await cartItemsEl.refreshCartItems();
              }
            }
            _atcLog('🗂️ cart page refresh DONE');
          }

          // Await the parallel /cart.js
          const newCartData = await newCartPromise;
          _atcLog('📥 /cart.js fetch DONE', { item_count: newCartData.item_count });

          if (window.ccPublishCartSnapshot) {
            window.ccPublishCartSnapshot(newCartData, 'quick-add-atc');
          }

          if (typeof window.ccDispatchCartChange === 'function') {
            window.ccDispatchCartChange({
              cart: newCartData,
              newTotalQuantity: newCartData.item_count,
              newTotalPrice: newCartData.total_price,
              source: 'quick-add-atc'
            });
          } else {
            document.dispatchEvent(new CustomEvent('on:cart:change', {
              bubbles: true,
              detail: { cart: newCartData, cartUpdated: true, source: 'quick-add-atc' }
            }));
          }

          // Disable cart loading states after refresh
          this.disableCartLoading();

          // Show success feedback on button (same as ProductForm)
          button.classList.add('is-success');
          button.innerHTML = '✓';
          _atcLog('✅ ATC COMPLETE (quick-add)');

          setTimeout(() => {
            button.classList.remove('is-loading');
            button.classList.remove('is-success');
            button.removeAttribute('aria-disabled');
            button.innerHTML = originalButtonContent;
          }, 1400);
        } else {
          _atcLog('❌ POST /cart/add returned error', { status: cartResponse.status });
          console.error('Failed to add to cart');
          // Reset button state on error
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
          button.innerHTML = originalButtonContent;
          // Disable cart loading states on error
          this.disableCartLoading();
        }
      } catch (error) {
        _atcLog('❌ ERROR', { message: error.message });
        console.error('Error adding product to cart:', error);
        // Reset button state on error
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
        button.innerHTML = originalButtonContent;
        // Disable cart loading states on error
        this.disableCartLoading();
      }
    }

    /**
     * Enables cart loading states (shows spinners on cart items)
     */
    enableCartLoading() {
      // Enable loading on cart drawer if it exists
      if (this.cartDrawer) {
        this.cartDrawer.classList.add('pointer-events-none');
        this.cartDrawer.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = false;
        });
      }
      
      // Enable loading on cart items if on cart page
      if (window.location.pathname === theme.routes.cart) {
        const cartItems = document.querySelector('cart-items');
        if (cartItems) {
          cartItems.classList.add('pointer-events-none');
          cartItems.querySelectorAll('.cart-item__loader').forEach((loader) => {
            loader.hidden = false;
          });
        }
      }
    }

    /**
     * Disables cart loading states (hides spinners on cart items)
     */
    disableCartLoading() {
      // Disable loading on cart drawer if it exists
      if (this.cartDrawer) {
        this.cartDrawer.classList.remove('pointer-events-none');
        this.cartDrawer.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = true;
        });
      }
      
      // Disable loading on cart items if on cart page
      if (window.location.pathname === theme.routes.cart) {
        const cartItems = document.querySelector('cart-items');
        if (cartItems) {
          cartItems.classList.remove('pointer-events-none');
          cartItems.querySelectorAll('.cart-item__loader').forEach((loader) => {
            loader.hidden = true;
          });
        }
      }
    }

    /**
     * Updates the cart count in the cart page title.
     * Accepts already-fetched cartData to avoid a redundant /cart.js request.
     */
    updateCartPageCount(cartData) {
      const cartTitle = document.querySelector('.js-cart-title');
      if (!cartTitle) return;

      let cartCount = cartTitle.querySelector('.cart__count');
      if (!cartCount) {
        cartCount = document.createElement('span');
        cartCount.className = 'cart__count';
        cartTitle.appendChild(document.createTextNode(' '));
        cartTitle.appendChild(cartCount);
      }

      const applyCount = (data) => {
        if (data.item_count > 0) {
          cartCount.textContent = `(${data.item_count})`;
        } else {
          cartCount.remove();
          const lastNode = cartTitle.lastChild;
          if (lastNode && lastNode.nodeType === Node.TEXT_NODE && lastNode.textContent === ' ') {
            lastNode.remove();
          }
        }
        if (typeof window.ccDispatchCartChange === 'function') {
          window.ccDispatchCartChange({
            cart: data,
            newTotalQuantity: data.item_count,
            newTotalPrice: data.total_price
          });
        }
      };

      if (cartData && cartData.item_count !== undefined) {
        // Use the data we already have — no extra fetch needed
        applyCount(cartData);
      } else {
        // Fallback: fetch if somehow called without data
        fetch(`${theme.routes.cart}.js`)
          .then(r => r.json())
          .then(applyCount)
          .catch(err => console.error('Error updating cart count:', err));
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}